/**
 * Minimal RDS PHI client for the NotesAI frontend.
 *
 * Built fresh on `diego8.0`, same philosophy as backend/api/phi_client.py:
 * starts with exactly one function (getRow), covering the Phase 1c pilot
 * on Schedules.js's refreshSelectedPatientFromDb only. Expand one
 * function at a time as later Phase 1c sites need more surface.
 *
 * This is the FIRST call site where RDS is reached directly from the
 * browser (every prior phase went through a Django proxy) -- RDS's CORS
 * config already allows http://localhost:3000 by default (see
 * RDS/main.py), so no RDS-side change was needed for local dev.
 *
 * Every call forwards the CALLING USER's own Supabase session token as
 * a bearer -- never a service-role key. RDS derives the viewer identity
 * from the verified JWT; this client has no way to override that.
 *
 * Row-level authorization is enforced by RDS on every read route
 * (rds_access.resolve() on the row's own user_id / shared_users plus the
 * viewer's tenant role; masked projection + access_level "none"
 * otherwise -- TEST_INTEGRATION_PLAN.md §11). This client sends only
 * ids and the user's bearer; it cannot claim ownership or force a raw
 * read.
 *
 * Phase 3 (TEST_INTEGRATION_PLAN.md §5) adds the two calls a real
 * cutover needs and nothing more: queryRows() -- the general
 * list/filter query the roster page issues -- and getCutoverFlags(),
 * which reads RDS's per-call-site cutover flags. Whether RDS's rows are
 * actually SHOWN is decided in rdsCutover.js, never here; this module
 * stays a dumb transport and still cannot bypass RDS's row-level
 * decisions (withheld rows are simply absent from queryRows' result).
 *
 * The independence switch (TEST_INTEGRATION_PLAN.md §31) adds the
 * source-mode calls (GET/POST /admin/source-mode, .../catchup), the
 * write-overlay transport (writeRow with `op`, deleteRowByBody,
 * reportWriteMiss) and one piece of memory: the last source mode RDS
 * reported (`getSourceMode`). That memory is sticky on purpose -- while
 * RDS is independent the write overlay must keep forwarding even when
 * the flag fetch fails, or a blip in RDS would silently drop writes that
 * nothing else re-syncs (§31 data #3).
 */
import { getSupabase } from "../components/supabaseClient";
import { rdsApiUrl } from "../components/env";
import {
  getLocalRdsSwitch, localRdsPausedSync, setLocalRdsSwitch as postLocalRdsSwitch,
} from "./rdsLocalSwitch";

export class PhiClientError extends Error {
  constructor(statusCode, detail) {
    super(`RDS returned ${statusCode}: ${detail || ""}`.slice(0, 500));
    this.statusCode = statusCode;
    this.detail = detail;
  }
}

export class PhiClientUnavailable extends Error {}

/**
 * Fetch one PHI row from RDS by primary key. Returns the row object on
 * success.
 *
 * @param {string} schema tenant schema, e.g. the clinic's schema name.
 * @param {string} table table name, e.g. "notes-c".
 * @param {string} rowId primary key value to look up.
 * @throws {Error} if schema/table/rowId are missing (never reaches the network).
 * @throws {PhiClientError} RDS responded with a non-2xx status.
 * @throws {PhiClientUnavailable} RDS could not be reached at all, or the
 *   caller has no active session to forward.
 */
export async function getRow(schema, table, rowId) {
  if (!schema || !table || !rowId) {
    throw new Error("schema, table, and rowId are all required");
  }

  const supabase = await getSupabase();
  const { data: { session } = {} } = await supabase.auth.getSession();
  const token = session?.access_token;
  if (!token) {
    throw new PhiClientUnavailable("no active session to forward to RDS");
  }

  let resp;
  try {
    resp = await fetch(
      `${rdsBaseUrl()}/phi/${encodeURIComponent(schema)}/${encodeURIComponent(table)}/${encodeURIComponent(rowId)}`,
      { headers: { Authorization: `Bearer ${token}` } },
    );
  } catch (err) {
    throw new PhiClientUnavailable(`RDS unreachable: ${err.message}`);
  }

  if (resp.ok) {
    try {
      return await resp.json();
    } catch (err) {
      throw new PhiClientError(
        resp.status,
        "RDS returned a non-JSON 200 response",
      );
    }
  }

  let detail = "";
  try {
    const body = await resp.json();
    detail = body?.detail || "";
  } catch (_) {
    detail = await resp.text().catch(() => "");
  }
  throw new PhiClientError(resp.status, detail);
}

/**
 * Batch-fetch specific, already-known rows from RDS by id (RDS's
 * POST /phi/{schema}/{table}/list). NOT a general list/filter query --
 * the caller must already have the ids (e.g. from the matching
 * Supabase query that already ran). See backend/api/phi_client.py's
 * list_rows() for the full design rationale -- this mirrors it.
 *
 * @param {string} schema tenant schema.
 * @param {string} table table name, e.g. "notes-c".
 * @param {string[]} ids row ids to fetch.
 * Authorization is decided entirely by RDS from each row's OWN
 * user_id / shared_users and the viewer's tenant role
 * (rds_access.resolve()). The browser never sends an owners map or an
 * include_ids bypass: either would let a caller claim any row.
 *
 * @param {{timeoutMs?: number}} [opts] abort after this many ms. Off by
 *   default: the request waits as long as the browser lets it, as it
 *   always has. The fire-and-forget shadow read passes a bound so a hung
 *   RDS cannot hold one of the browser's six connections to it
 *   indefinitely; an abort is PhiClientUnavailable("RDS timed out"), as
 *   in queryRows.
 * @returns {Promise<object[]>} RDS's rows array (each with an
 *   access_level field), or [] if ids is empty (no network call made).
 */
export async function listRows(schema, table, ids, { timeoutMs } = {}) {
  if (!schema || !table) throw new Error("schema and table are required");
  const cleanIds = (ids || []).filter(Boolean).map(String);
  if (!cleanIds.length) return [];

  const supabase = await getSupabase();
  const { data: { session } = {} } = await supabase.auth.getSession();
  const token = session?.access_token;
  if (!token) {
    throw new PhiClientUnavailable("no active session to forward to RDS");
  }

  const controller = timeoutMs ? new AbortController() : null;
  const timer = controller ? setTimeout(() => controller.abort(), timeoutMs) : null;
  const timedOut = (err) => controller !== null && err?.name === "AbortError";
  try {
    let resp;
    try {
      resp = await fetch(
        `${rdsBaseUrl()}/phi/${encodeURIComponent(schema)}/${encodeURIComponent(table)}/list`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ ids: cleanIds }),
          ...(controller ? { signal: controller.signal } : {}),
        },
      );
    } catch (err) {
      if (timedOut(err)) throw new PhiClientUnavailable("RDS timed out");
      throw new PhiClientUnavailable(`RDS unreachable: ${err.message}`);
    }

    if (resp.ok) {
      try {
        const body = await resp.json();
        return body.rows || [];
      } catch (err) {
        // The timer keeps running while the body streams, so an abort can
        // also land here -- still a timeout, not a bad response.
        if (timedOut(err)) throw new PhiClientUnavailable("RDS timed out");
        throw new PhiClientError(
          resp.status,
          "RDS returned a non-JSON 200 response",
        );
      }
    }

    let detail2 = "";
    try {
      const body = await resp.json();
      detail2 = body?.detail || "";
    } catch (_) {
      detail2 = await resp.text().catch(() => "");
    }
    throw new PhiClientError(resp.status, detail2);
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Read an error response's `detail` the way listRows/getRow do -- JSON
 * body first, raw text as a fallback -- so every route maps a non-2xx
 * to the same PhiClientError shape.
 */
async function readErrorDetail(resp) {
  return (await readErrorBody(resp)).detail;
}

/**
 * Like readErrorDetail, but also hands back the parsed JSON body (or null)
 * so an operator call can keep RDS's structured, PHI-free refusal fields:
 * POST /admin/source-mode answers 409 with `blockers`, the forced-flag and
 * refused-sync 409s carry `forced` / `refused`, and /write's 409 carries
 * `reason` (plan §31). Never throws.
 */
async function readErrorBody(resp) {
  try {
    const body = await resp.json();
    return {
      detail: body?.detail || "",
      body: body && typeof body === "object" && !Array.isArray(body) ? body : null,
    };
  } catch (_) {
    return { detail: await resp.text().catch(() => ""), body: null };
  }
}

/** A PhiClientError for a non-2xx operator answer, annotated with RDS's
 * structured refusal fields when the body carries them (strings and
 * booleans only -- never row content). */
function operatorError(status, parsed) {
  const err = new PhiClientError(status, parsed.detail);
  const body = parsed.body;
  if (body) {
    if (Array.isArray(body.blockers)) err.blockers = body.blockers.map((b) => String(b).slice(0, 300));
    if (typeof body.reason === "string") err.reason = body.reason.slice(0, 120);
    if (body.forced === true) err.forced = true;
    if (body.refused === true) err.refused = true;
  }
  return err;
}

/**
 * General list/filter query against RDS (POST /phi/{schema}/{table}/query)
 * -- the Phase-3 counterpart of the roster page's Supabase query. Unlike
 * listRows() the caller does NOT already know the ids: RDS applies the
 * ordering/search itself, resolves each row's access_level with
 * rds_access.resolve(), and WITHHOLDS rows the viewer is not entitled to
 * (they are counted in masked_count, never returned, unless the body
 * asks for include_masked -- which the roster cutover never does).
 *
 * A hard timeout is mandatory here, unlike the shadow reads: this call
 * sits on the page's critical path when the cutover is effective, so a
 * hung RDS must degrade into the Supabase fallback (rdsCutover.js) in
 * bounded time rather than hang the roster. An abort surfaces as
 * PhiClientUnavailable("RDS timed out").
 *
 * @param {string} schema tenant schema.
 * @param {string} table table name, e.g. "patients_with_ehrid".
 * @param {object} body RDS query body: {limit, offset, order: {column,
 *   ascending, nulls_first}, search: {columns, value}, include_masked}.
 *   Sent verbatim -- RDS validates it (400 on a bad column / value).
 * @param {{timeoutMs?: number}} [opts] abort after this many ms (8s).
 * @returns {Promise<{rows: object[], count: number, masked_count: number,
 *   limit: number, offset: number, truncated: boolean}>} RDS's response;
 *   `rows` each carry an access_level field, `truncated` means the page
 *   was full (fetched >= limit, counting withheld rows).
 * @throws {PhiClientError} RDS responded with a non-2xx status.
 * @throws {PhiClientUnavailable} RDS unreachable, timed out, or no
 *   session to forward.
 */
export async function queryRows(
  schema,
  table,
  body,
  { timeoutMs = 8000, signal } = {},
) {
  if (!schema || !table) throw new Error("schema and table are required");
  // A caller that has moved on (a newer roster fetch superseded this one)
  // can abort through `signal`; RDS then stops the query instead of
  // finishing a 1000-row page and its audit rows for nobody.
  if (signal?.aborted) throw new PhiClientUnavailable("RDS query aborted");

  const supabase = await getSupabase();
  const { data: { session } = {} } = await supabase.auth.getSession();
  const token = session?.access_token;
  if (!token) {
    throw new PhiClientUnavailable("no active session to forward to RDS");
  }

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  const onOuterAbort = () => controller.abort();
  if (signal) signal.addEventListener("abort", onOuterAbort, { once: true });
  const abortReason = () =>
    signal?.aborted ? "RDS query aborted" : "RDS timed out";
  try {
    let resp;
    try {
      resp = await fetch(
        `${rdsBaseUrl()}/phi/${encodeURIComponent(schema)}/${encodeURIComponent(table)}/query`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(body || {}),
          signal: controller.signal,
        },
      );
    } catch (err) {
      if (err?.name === "AbortError")
        throw new PhiClientUnavailable(abortReason());
      throw new PhiClientUnavailable(`RDS unreachable: ${err.message}`);
    }

    if (resp.ok) {
      let parsed;
      try {
        parsed = await resp.json();
      } catch (err) {
        // The timer keeps running while the body streams, so an abort
        // can also land here -- still a timeout, not a bad response.
        if (err?.name === "AbortError")
          throw new PhiClientUnavailable(abortReason());
        throw new PhiClientError(
          resp.status,
          "RDS returned a non-JSON 200 response",
        );
      }
      const rows = Array.isArray(parsed?.rows) ? parsed.rows : [];
      return {
        rows,
        count: Number.isFinite(parsed?.count) ? parsed.count : rows.length,
        masked_count: Number.isFinite(parsed?.masked_count)
          ? parsed.masked_count
          : 0,
        limit: parsed?.limit,
        offset: parsed?.offset,
        truncated: parsed?.truncated === true,
      };
    }

    throw new PhiClientError(resp.status, await readErrorDetail(resp));
  } finally {
    clearTimeout(timer);
    if (signal) signal.removeEventListener("abort", onOuterAbort);
  }
}

// Last GET /cutover outcome. A GOOD payload is reused for `maxAgeMs` so a
// page that re-fetches on every keystroke does not turn into a flag poll.
// A FAILURE is remembered for `failureMaxAgeMs` as well (a shorter window):
// this lookup sits on the roster's critical path before the Supabase query,
// so with RDS down every keystroke would otherwise wait out the timeout.
let cutoverCache = null; // { payload, fetchedAt, failed }
// The GET /cutover in flight, shared by every caller that arrives while it
// runs: an initial load fires several overlaid reads at once, and as
// separate requests they took longer together than the timeout, which
// failed every one of them closed.
let cutoverInflight = null;

/** Test hook -- forget the cached GET /cutover outcome. */
export function _resetCutoverCache() {
  cutoverCache = null;
  cutoverInflight = null;
}

/**
 * Read RDS's cutover flags (GET /cutover). The payload is
 * {kill_switch, flags: {<call_site>: {enabled, effective, reason, table,
 * note, updated_at}}}; callers must act on `effective` ONLY -- it is
 * `enabled` AND no kill switch AND the mirror for the flag's table is
 * fresh, all decided server-side.
 *
 * FAILS CLOSED: on ANY failure (no session, network error, timeout,
 * non-2xx, non-JSON, or RDS itself reporting its flags unavailable) the
 * result is {kill_switch: true, flags: {}, error: "<class or status>"}
 * -- i.e. "nothing is effective", so the caller keeps serving Supabase.
 * Never throws.
 *
 * SINGLE-FLIGHT: a caller that finds no usable cache while a request is
 * already in flight waits for that request instead of sending its own
 * (it then shares that request's timeout), so a tab sends at most one
 * GET /cutover at a time.
 *
 * @param {{maxAgeMs?: number, failureMaxAgeMs?: number, timeoutMs?: number}}
 *   [opts] reuse the last good payload for maxAgeMs (30s) and a failed
 *   lookup for failureMaxAgeMs (15s); abort the request after timeoutMs
 *   (2.5s -- this is awaited on the roster's critical path).
 */
export async function getCutoverFlags({
  maxAgeMs = 30000,
  failureMaxAgeMs = 15000,
  timeoutMs = 2500,
} = {}) {
  if (cutoverCache) {
    const age = Date.now() - cutoverCache.fetchedAt;
    if (age < (cutoverCache.failed ? failureMaxAgeMs : maxAgeMs))
      return cutoverCache.payload;
  }
  if (!cutoverInflight) {
    const inflight = fetchCutoverFlags(timeoutMs).finally(() => {
      // Only if it is still this request's slot: a flag flip meanwhile may
      // have let a newer request take it.
      if (cutoverInflight === inflight) cutoverInflight = null;
    });
    cutoverInflight = inflight;
  }
  return cutoverInflight;
}

/**
 * The payload every consumer gets while RDS is disabled for THIS NotesAI
 * (rdsLocalSwitch.js): exactly what a paused RDS answers -- kill switch
 * on, nothing effective, source mode supabase_only, no bucket owned -- so
 * overlays, per-screen cutovers and the write forwarding behave as they
 * already do for an RDS-side pause. `local_switch` tells the two apart.
 * Cached as a GOOD payload (it is the truth for this deployment) and the
 * sticky mode follows, so queued forwards drain instead of retrying.
 */
function locallyPausedPayload(local) {
  const payload = {
    kill_switch: true,
    flags: {},
    source_mode: "supabase_only",
    storage_buckets: [],
    local_switch: local,
  };
  cutoverCache = { payload, fetchedAt: Date.now(), failed: false };
  rememberSourceMode("supabase_only");
  return payload;
}

/**
 * One GET /cutover, recorded in cutoverCache (see getCutoverFlags) -- after
 * this NotesAI's own switch has been consulted. When the pause is already
 * known (cache / sticky) the switch is confirmed first and RDS is not
 * asked at all; otherwise the two lookups run side by side so the roster's
 * critical path pays no extra round trip, and the switch wins.
 */
async function fetchCutoverFlags(timeoutMs) {
  let local = null;
  let localPending = getLocalRdsSwitch({ timeoutMs });
  if (localRdsPausedSync()) {
    local = await localPending;
    localPending = null;
    if (local?.disabled) return locallyPausedPayload(local);
  }
  const rds = await fetchRdsCutoverFlags(timeoutMs);
  if (localPending) local = await localPending;
  if (local?.disabled) return locallyPausedPayload(local);
  if (rds.error !== undefined) {
    const payload = { kill_switch: true, flags: {}, error: rds.error };
    cutoverCache = { payload, fetchedAt: Date.now(), failed: true };
    return payload;
  }
  const payload = rds.payload;
  cutoverCache = { payload, fetchedAt: Date.now(), failed: false };
  // A GOOD payload is the one place the source mode is learnt from in
  // normal operation (§31 7.1); an older RDS sends no source_mode and
  // the sticky value is simply left alone.
  rememberSourceMode(payload.source_mode);
  return payload;
}

/** The raw GET /cutover: {payload} on a good answer, {error} otherwise. */
async function fetchRdsCutoverFlags(timeoutMs) {
  const closed = (error) => ({ error: String(error).slice(0, 200) });

  let token;
  try {
    const supabase = await getSupabase();
    const { data: { session } = {} } = await supabase.auth.getSession();
    token = session?.access_token;
  } catch (err) {
    return closed(err?.name || "session error");
  }
  if (!token) return closed("no session");

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    let resp;
    try {
      resp = await fetch(`${rdsBaseUrl()}/cutover`, {
        headers: { Authorization: `Bearer ${token}` },
        signal: controller.signal,
      });
    } catch (err) {
      return closed(
        err?.name === "AbortError" ? "timeout" : err?.name || "fetch error",
      );
    }
    if (!resp.ok) return closed(resp.status);

    let payload;
    try {
      payload = await resp.json();
    } catch (err) {
      return closed(
        err?.name === "AbortError" ? "timeout" : "non-JSON response",
      );
    }
    // RDS answers 200 with flags: {} + error when its own flag table is
    // unreadable -- not a payload worth caching or trusting.
    if (payload?.error) return closed(payload.error);
    if (
      !payload ||
      typeof payload.flags !== "object" ||
      payload.flags === null
    ) {
      return closed("malformed payload");
    }
    return { payload };
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Flip THIS NotesAI's RDS switch (rdsLocalSwitch.setLocalRdsSwitch) and
 * forget the cached cutover flags, so the next overlaid read in this tab
 * already behaves the new way. RDS's own mode is untouched: this is the
 * "Use Supabase only" / "Enable RDS" pair in the panel since 2026-10-24.
 */
export async function setLocalRdsSwitch(disabled, opts) {
  const state = await postLocalRdsSwitch(disabled, opts);
  cutoverCache = null;
  cutoverInflight = null;
  if (disabled) rememberSourceMode("supabase_only");
  return state;
}

export { getLocalRdsSwitch };

/**
 * Start the GET /cutover lookup as soon as there is a session to send
 * (supabaseClient.js calls this right after the schema load), so the
 * page's first overlaid read finds the flags cached -- or joins the
 * request in flight -- instead of starting it only once its own Supabase
 * reads are done. Without a session nothing is fetched: a "no session"
 * failure would otherwise be cached for the first real caller. Gated on
 * the same check as every cutover: this deployment has an RDS_API_URL.
 * Never throws.
 */
export async function prefetchCutoverFlags() {
  try {
    if (!rdsBaseUrl()) return;
    if (!(await getSessionToken())) return;
    await getCutoverFlags();
  } catch (_) {
    // Only a head start: the first overlaid read asks for the flags anyway.
  }
}

// ---------------------------------------------------------------------------
// Sticky source mode (TEST_INTEGRATION_PLAN.md §31, brief 7.1). RDS reports
// `source_mode` ("mirror" | "independent" | "supabase_only") on every GET
// /cutover; the last
// value seen is kept in memory AND in localStorage so that, when the flag
// fetch fails (RDS blip, expired session), the write overlay still knows
// it must forward (data #3: independent-but-flags-unreachable still
// forwards -- nothing else re-syncs a dropped write once the whole-table
// mirror has stopped). The value is a mode name and a timestamp, never
// PHI, and it expires after 24 h so a stale browser cannot claim
// independence forever after a rollback. "supabase_only" (RDS paused) is
// remembered like the others: an unknown value would otherwise leave an
// older "independent" in place and keep forwarding writes to a paused RDS.
// ---------------------------------------------------------------------------
const SOURCE_MODE_STORAGE_KEY = "rds_source_mode";
const SOURCE_MODE_TTL_MS = 24 * 60 * 60 * 1000;
const SOURCE_MODES = new Set(["mirror", "independent", "supabase_only"]);

function readStoredSourceMode() {
  try {
    const raw = window.localStorage.getItem(SOURCE_MODE_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!SOURCE_MODES.has(parsed?.mode) || !Number.isFinite(parsed?.at)) return null;
    return { mode: parsed.mode, at: parsed.at };
  } catch (_) {
    // Private mode, blocked storage, or garbage: no memory, not an error.
    return null;
  }
}

let lastKnownSourceMode = readStoredSourceMode(); // { mode, at } | null

function rememberSourceMode(mode) {
  if (!SOURCE_MODES.has(mode)) return;
  lastKnownSourceMode = { mode, at: Date.now() };
  try {
    window.localStorage.setItem(SOURCE_MODE_STORAGE_KEY, JSON.stringify(lastKnownSourceMode));
  } catch (_) {
    // Memory alone is fine for this tab; storage is only the reload bridge.
  }
}

/** The sticky mode when it is younger than the TTL, else null. */
function stickySourceMode() {
  if (!lastKnownSourceMode) return null;
  if (Date.now() - lastKnownSourceMode.at > SOURCE_MODE_TTL_MS) return null;
  return lastKnownSourceMode.mode;
}

/** Test hook -- forget the sticky source mode (memory and storage). */
export function _resetSourceModeCache() {
  lastKnownSourceMode = null;
  try {
    window.localStorage.removeItem(SOURCE_MODE_STORAGE_KEY);
  } catch (_) {
    // nothing to forget
  }
}

/**
 * Which side holds the truth for PHI right now: "mirror",
 * "independent" or "supabase_only" (RDS paused: the app uses Supabase alone). With `fresh` (the default) RDS's cutover payload is
 * consulted first (through getCutoverFlags' cache, so this is not a
 * poll); a payload that FAILED (closed) or predates source modes falls
 * through to the sticky value (<= 24 h old), and with no memory at all
 * the answer is "mirror" -- today's behaviour. Never throws.
 *
 * @param {{fresh?: boolean}} [opts] fresh=false skips RDS and answers
 *   from memory only.
 * @returns {Promise<"mirror"|"independent"|"supabase_only">}
 */
export async function getSourceMode({ fresh = true } = {}) {
  if (fresh) {
    try {
      const payload = await getCutoverFlags();
      if (!payload?.error && SOURCE_MODES.has(payload?.source_mode)) {
        return payload.source_mode;
      }
    } catch (_) {
      // fall through to the sticky value
    }
  }
  return stickySourceMode() || "mirror";
}

// ---------------------------------------------------------------------------
// Operator calls (SuperAdmin -> HIPAA -> RDS Migration). Same transport as
// the reads: the CALLING USER's own session token, nothing else. Whether
// that user may operate RDS is RDS's decision (_require_operator); this
// module only forwards the bearer and surfaces the answer.
// ---------------------------------------------------------------------------

/** The signed-in user's Supabase access token, or null when there is no
 * session. Exported for the "Open RDS admin" hand-off (the token travels
 * in the URL FRAGMENT, which browsers never send to the server). */
export async function getSessionToken() {
  try {
    const supabase = await getSupabase();
    const { data: { session } = {} } = await supabase.auth.getSession();
    return session?.access_token || null;
  } catch (_) {
    return null;
  }
}

/** Base URL of the RDS service this build talks to. */
export function rdsBaseUrl() {
  return rdsApiUrl;
}

/**
 * One authenticated JSON request to RDS with a hard timeout -- the
 * transport every operator call and every write-overlay call shares.
 * The signed-in user's own bearer, nothing else; RDS decides what that
 * user may do. A 204 (RDS's "recorded, nothing to say" answer, e.g.
 * /audit/write-miss) resolves to null; any other 2xx must be JSON.
 *
 * @param {"GET"|"POST"|"DELETE"} method HTTP method.
 * @param {string} path RDS path, e.g. "/admin/source-mode".
 * @param {object} [body] JSON body (omitted when undefined).
 * @param {{timeoutMs?: number}} [opts] abort after this many ms (8s).
 * @throws {PhiClientError} non-2xx with RDS's detail;
 *   {PhiClientUnavailable} no session, unreachable, or timed out.
 */
export async function adminRequest(method, path, body, { timeoutMs = 8000, cache = "no-store" } = {}) {
  const token = await getSessionToken();
  if (!token)
    throw new PhiClientUnavailable("no active session to forward to RDS");
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    let resp;
    try {
      resp = await fetch(`${rdsBaseUrl()}${path}`, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: body === undefined ? undefined : JSON.stringify(body),
        signal: controller.signal,
        // A polled GET must never be served from the HTTP cache: the same URL
        // is asked for every second while a scan runs, and a cached copy is
        // a progress bar frozen at whatever it first saw.
        cache,
      });
    } catch (err) {
      if (err?.name === "AbortError")
        throw new PhiClientUnavailable("RDS timed out");
      throw new PhiClientUnavailable(`RDS unreachable: ${err.message}`);
    }
    if (resp.ok) {
      if (resp.status === 204) return null;
      try {
        return await resp.json();
      } catch (err) {
        if (err?.name === "AbortError")
          throw new PhiClientUnavailable("RDS timed out");
        throw new PhiClientError(
          resp.status,
          "RDS returned a non-JSON 200 response",
        );
      }
    }
    throw operatorError(resp.status, await readErrorBody(resp));
  } finally {
    clearTimeout(timer);
  }
}

/** POST shorthand over adminRequest (the shape every pre-§31 call used). */
async function adminPost(path, body, opts) {
  return adminRequest("POST", path, body, opts);
}

/**
 * Flip one seeded cutover flag (POST /admin/cutover). Returns RDS's new
 * flag payload (same shape as GET /cutover) and forgets the cached flags
 * so the next isCutoverEffective() sees the change at once.
 *
 * @param {string} callSite e.g. "patient_list".
 * @param {boolean} enabled the operator's intent.
 * @param {string} [note] optional audit note (RDS keeps 500 chars).
 * @param {{timeoutMs?: number, force?: boolean}} [opts] `force` is sent
 *   in the body: while RDS is independent an overlay_/write_ flag is
 *   forced on and can only be disabled with force AND a note (§31
 *   clinical #9); RDS answers 409 otherwise.
 * @throws {PhiClientError} 401/403 (not an operator), 404 (unknown flag)
 *   or any other non-2xx; {PhiClientUnavailable} no session / unreachable.
 */
export async function setCutoverFlag(
  callSite,
  enabled,
  note,
  { timeoutMs = 8000, force } = {},
) {
  if (!callSite) throw new Error("callSite is required");
  const body = { call_site: callSite, enabled: !!enabled };
  if (note) body.note = String(note);
  if (force) body.force = true;
  const payload = await adminPost("/admin/cutover", body, { timeoutMs });
  cutoverCache = null;
  // A GET begun before the flip must not answer the next caller.
  cutoverInflight = null;
  return payload;
}

/**
 * Refresh the PHI data mirror now (POST /admin/sync-mirror), then the
 * access mirror -- what the scheduler does every RDS_MIRROR_SYNC_INTERVAL_SEC.
 * RDS answers when the run has finished, and its own bound on a run is
 * 15 minutes (the "final full refresh" before going independent must be
 * allowed to finish, §31 ops #3), so the client waits that long too.
 * 409 = a run is already going. While RDS is independent the same route
 * runs the catch-up reconciliation + access sync instead.
 */
export async function syncMirror({ timeoutMs = 900000 } = {}) {
  return adminPost("/admin/sync-mirror", undefined, { timeoutMs });
}

/**
 * Upsert one PHI row in RDS (POST /phi/{schema}/{table}/write) -- Phase D
 * (TEST_INTEGRATION_PLAN.md §25). Same transport as the operator calls:
 * the signed-in user's own session token, RDS decides with resolve()
 * whether this viewer may write the row (403 otherwise), and columns the
 * mirror does not hold are dropped server-side. Returns RDS's payload
 * (`written`, `reason`, `row` -- §31: `written: false` with reason
 * "missing" (op update on a row RDS lacks) or "stale" (the row's
 * updated_at/last_updated is newer in RDS) is a 200, not an error).
 *
 * @param {{op?: "insert"|"update"|"upsert", timeoutMs?: number}} [opts]
 *   `op` is sent alongside the row when given (RDS defaults to upsert);
 *   "update" never inserts a partial row (§31 data #2).
 * @throws {PhiClientError} non-2xx (403 no write access, 404 table not
 *   mirrored, 400 nothing to write, 409 no usable primary key);
 *   {PhiClientUnavailable} no session, unreachable, or timed out.
 */
export async function writeRow(schema, table, row, { op, timeoutMs = 8000 } = {}) {
  if (!schema || !table) throw new Error("schema and table are required");
  if (!row || typeof row !== "object" || Array.isArray(row))
    throw new Error("row must be an object");
  const body = op ? { row, op } : { row };
  return adminPost(
    `/phi/${encodeURIComponent(schema)}/${encodeURIComponent(table)}/write`,
    body,
    { timeoutMs },
  );
}

/**
 * Delete one PHI row in RDS located by its body (POST
 * /phi/{schema}/{table}/delete, §31 2.6): RDS finds the row by primary
 * key, else by id / ehr_id / email from `row`, authorises exactly as the
 * path-based DELETE does, and answers {deleted, key}. Used by the write
 * overlay, which has Supabase's returned row but not always a PK column.
 *
 * @throws {PhiClientError} 404 absent, 403 no write access, other non-2xx;
 *   {PhiClientUnavailable} no session, unreachable, or timed out.
 */
export async function deleteRowByBody(schema, table, row, { timeoutMs = 8000 } = {}) {
  if (!schema || !table) throw new Error("schema and table are required");
  if (!row || typeof row !== "object" || Array.isArray(row))
    throw new Error("row must be an object");
  return adminPost(
    `/phi/${encodeURIComponent(schema)}/${encodeURIComponent(table)}/delete`,
    { row },
    { timeoutMs },
  );
}

/**
 * Record a lost forward in RDS's audit log (POST /audit/write-miss, §31
 * clinical #11): the write overlay exhausted its retries for one row, so
 * RDS may now lack a write Supabase holds. Ids and a short reason only,
 * never row content. Fire-and-forget: never throws, resolves to true
 * when RDS recorded it.
 */
export async function reportWriteMiss(schema, table, rowId, reason) {
  try {
    await adminPost("/audit/write-miss", {
      schema: String(schema),
      table: String(table),
      row_id: String(rowId),
      reason: String(reason || "unknown").slice(0, 120),
    }, { timeoutMs: 8000 });
    return true;
  } catch (_) {
    return false;
  }
}

// ---------------------------------------------------------------------------
// Source-mode calls (§31 2.5). All superadmin-only on RDS's side; this
// module forwards the bearer and surfaces the answer (403 = not a
// superadmin in RDS's access mirror, 409 = blockers / pinned / wrong mode).
// ---------------------------------------------------------------------------

/**
 * GET /admin/source-mode: the current mode plus the preconditions,
 * blockers and forceable_blockers the switch dialog lists. The route
 * inspects RDS's catalog (primary keys per PHI table), so it gets more
 * patience than a flag flip.
 */
export async function getSourceModeStatus({ timeoutMs = 15000 } = {}) {
  return adminRequest("GET", "/admin/source-mode", undefined, { timeoutMs });
}

/**
 * POST /admin/source-mode: switch RDS to "independent", back to "mirror"
 * (re-clone RDS from Supabase) or to "supabase_only" (pause RDS, keep its
 * data). `confirm` must equal RDS's typed phrase for the target mode
 * (INDEPENDENT / MIRROR / SUPABASE); `force` needs a non-empty `note`. Forgets the
 * cached flags (the switch flips every overlay_/write_ flag on) and
 * updates the sticky mode from RDS's answer. Returns RDS's payload
 * ({changed, source_mode, cutover?, warning?}).
 *
 * @throws {PhiClientError} 400 bad confirm/mode, 403 not superadmin,
 *   409 blockers (detail + blockers) or pinned; {PhiClientUnavailable}.
 */
export async function setSourceMode(
  mode,
  { confirm, note, catchup, force, timeoutMs = 60000 } = {},
) {
  if (!SOURCE_MODES.has(mode)) throw new Error("mode must be mirror, independent or supabase_only");
  const body = { mode, confirm: String(confirm || "") };
  if (note) body.note = String(note);
  if (catchup !== undefined) body.catchup = !!catchup;
  if (force) body.force = true;
  const payload = await adminPost("/admin/source-mode", body, { timeoutMs });
  cutoverCache = null;
  cutoverInflight = null; // as in setCutoverFlag
  rememberSourceMode(payload?.source_mode?.mode || mode);
  return payload;
}

/**
 * POST /admin/repair-keys: recreate one mirrored PHI table keyed on `id`
 * in every tenant -- the fix for the switch dialog's hard blocker "no
 * primary key on id" (plan §31). RDS drops its copy of the table and
 * refills it from Supabase keyed on id (mirror_sync.py --recreate), so it
 * only works in mirror mode (409 `refused` while independent) and holds
 * the mirror lock (409 while a sync runs). A whole-table refresh can take
 * minutes: the timeout matches syncMirror's. `dryRun` reports without
 * writing. Returns RDS's payload ({ok, summary, error?, table, dry_run,
 * tables_without_id_pk}).
 */
export async function repairKeys(table, { dryRun = false, timeoutMs = 900000 } = {}) {
  if (!table) throw new Error("table is required");
  return adminPost("/admin/repair-keys", { table: String(table), dry_run: !!dryRun }, { timeoutMs });
}

/**
 * POST /admin/source-mode/catchup: turn the catch-up reconciliation on or
 * off while independent (409 in mirror mode). Turning it off needs a
 * non-empty note on RDS's side (it is only safe once the backend's
 * Supabase-only writers are routed to RDS, plan §24.1).
 */
export async function setCatchup(enabled, { note, timeoutMs = 8000 } = {}) {
  const body = { enabled: !!enabled };
  if (note) body.note = String(note);
  return adminPost("/admin/source-mode/catchup", body, { timeoutMs });
}

/**
 * POST /admin/two-way-sync/preview: compare RDS and Supabase row by row
 * and return what a two-way sync would do -- {tables, items, totals,
 * pending, truncated, values_included, source_mode, mirror_warning,
 * confirm_phrase}. Each item names its row (schema, table, id), the
 * proposed `action` (update_rds / update_supabase / merge / insert_* /
 * delete_* / conflict), the columns travelling each way, and the
 * `fingerprint` the apply must echo back. Writes no data. Reads every
 * mirrored table from both sides, so it gets minutes, not seconds.
 *
 * @param {{schemas?: string[], tables?: string[], timeoutMs?: number}} [opts]
 * @throws {PhiClientError} 403 (role), 400; {PhiClientUnavailable}.
 */
export async function previewTwoWaySync({ schemas, tables, timeoutMs = 300000 } = {}) {
  const body = {};
  if (schemas?.length) body.schemas = schemas.map(String);
  if (tables?.length) body.tables = tables.map(String);
  return adminPost("/admin/two-way-sync/preview", body, { timeoutMs });
}

/**
 * POST /admin/two-way-sync/preview/start: begin the same comparison the
 * blocking preview does, on RDS's side, and return {job_id, poll_url}
 * straight away.
 *
 * This is what the panel uses instead of previewTwoWaySync(), because a first
 * scan reads every row of every mirrored table from both sides and a single
 * blocking request gives the operator nothing to look at for the minutes it
 * takes. The answer is identical; only the wait is reported.
 *
 * @throws {PhiClientError} 403 (role), 400 bad table, 409 a preview is
 *   already running or RDS is independent; {PhiClientUnavailable}.
 */
export async function startTwoWaySyncPreview({ schemas, tables, timeoutMs = 60000 } = {}) {
  const body = {};
  if (schemas?.length) body.schemas = schemas.map(String);
  if (tables?.length) body.tables = tables.map(String);
  return adminPost("/admin/two-way-sync/preview/start", body, { timeoutMs });
}

/**
 * GET /admin/two-way-sync/preview/{job_id}: how far a background preview has
 * got -- {state, tables_total, tables_done, rows_read, rows_expected,
 * percent, eta_sec, elapsed_sec, tables, done, result}. `result` is the
 * body previewTwoWaySync() would have returned, and arrives only once
 * `state` is "done".
 *
 * @throws {PhiClientError} 403 (role), 404 the job is gone (RDS keeps a
 *   finished one only for a while -- start a new one); {PhiClientUnavailable}.
 */
export async function twoWaySyncPreviewProgress(jobId, { timeoutMs = 30000 } = {}) {
  if (!jobId) throw new Error("jobId is required");
  return adminRequest(
    "GET",
    `/admin/two-way-sync/preview/${encodeURIComponent(jobId)}`,
    undefined,
    { timeoutMs },
  );
}

/**
 * POST /admin/two-way-sync/apply: apply the reviewed decisions
 * ([{schema, table, id, resolution: "auto"|"rds"|"supabase"|"latest"|"skip",
 * fingerprint}]). `confirm` must be RDS's typed phrase (SYNC). RDS
 * re-compares first and refuses any row that changed since the preview
 * (`stale`). Returns {ok, counts, results}.
 *
 * @throws {PhiClientError} 400 bad phrase/decisions, 403 role, 409 a
 *   mirror sync is running; {PhiClientUnavailable}.
 */
export async function applyTwoWaySync(decisions, { confirm, timeoutMs = 900000 } = {}) {
  if (!Array.isArray(decisions) || !decisions.length) throw new Error("decisions are required");
  return adminPost(
    "/admin/two-way-sync/apply",
    { confirm: String(confirm || ""), decisions },
    { timeoutMs },
  );
}

/**
 * POST /admin/two-way-sync/buckets/preview: the same comparison for the
 * Storage buckets RDS mirrors, file by file (Supabase eTag vs RDS md5 /
 * source eTag; nothing is downloaded). Items have the table items' shape
 * with `kind: "object"`, `table` = bucket and `id` = path. Listing every
 * bucket takes about a minute.
 *
 * The blocking call above is what this used to be; the panel now uses
 * startTwoWayBucketsPreview() below, which is the same scan reported while it
 * runs. Kept because it is a real route and the scripts/tests use it.
 */
export async function previewTwoWayBuckets({ buckets, timeoutMs = 900000 } = {}) {
  const body = {};
  if (buckets?.length) body.buckets = buckets.map(String);
  return adminPost("/admin/two-way-sync/buckets/preview", body, { timeoutMs });
}

/**
 * POST /admin/two-way-sync/buckets/preview/start: begin the file comparison on
 * RDS's side and return {job_id, poll_url} straight away; poll
 * twoWaySyncPreviewProgress(job_id) for how far it has got, and the plan
 * arrives as `result` once `state` is "done".
 *
 * This is what the panel's Files mode uses, for the same reason Tables uses
 * the table equivalent: listing a bucket is one request per folder, so a real
 * comparison is minutes of one blocking call and nothing to look at. The
 * answer is identical to previewTwoWayBuckets()'; only the wait is reported.
 * Its `tables_total` counts BUCKETS, and each of them is reported under the
 * name `bucket.<name>`.
 *
 * @throws {PhiClientError} 403 (role), 400 bad bucket, 409 a preview is
 *   already running; {PhiClientUnavailable}.
 */
export async function startTwoWayBucketsPreview({ buckets, timeoutMs = 60000 } = {}) {
  const body = {};
  if (buckets?.length) body.buckets = buckets.map(String);
  return adminPost("/admin/two-way-sync/buckets/preview/start", body, { timeoutMs });
}

/**
 * POST /admin/two-way-sync/buckets/apply: [{bucket, path, resolution,
 * fingerprint, action}] with the typed phrase. Files are copied whole, so
 * this can take minutes for audio. 409 while a bucket sync is running.
 */
export async function applyTwoWayBuckets(decisions, { confirm, timeoutMs = 1800000 } = {}) {
  if (!Array.isArray(decisions) || !decisions.length) throw new Error("decisions are required");
  return adminPost(
    "/admin/two-way-sync/buckets/apply",
    { confirm: String(confirm || ""), decisions },
    { timeoutMs },
  );
}

/**
 * POST /admin/two-way-sync/buckets/file-diff: what a bucket sync would change
 * inside one .json file, GitHub-style -- `changes` (field paths added /
 * removed / changed, with before and after) and `hunks` (unified line diff
 * with old/new line numbers). "Before" is `fromSide`'s copy (the one the sync
 * overwrites), "after" the other side's. `includeDocs` adds both parsed
 * documents ({documents: {before, after}}) for the note / merge view;
 * `lines: false` skips the line diff (no `hunks`), which a note shown as a
 * document does not need.
 * Returns file contents; RDS audits it.
 *
 * @throws {PhiClientError} 400 not .json / bad side, 403 role, 422 too big;
 *   {PhiClientUnavailable}.
 */
export async function twoWayBucketFileDiff(
  bucket, path, { fromSide = "rds", context = 3, includeDocs = false, lines = true, timeoutMs = 60000 } = {},
) {
  return adminPost(
    "/admin/two-way-sync/buckets/file-diff",
    { bucket: String(bucket), path: String(path), from_side: fromSide, context, include_docs: !!includeDocs, lines: lines !== false },
    { timeoutMs },
  );
}

/**
 * Rows by id for the PHI overlay (POST /phi/{schema}/{table}/list, plan
 * §28): each row comes back RAW when rds_access.resolve() entitles the
 * viewer, MASKED (access_level "none", [REDACTED:*] values) otherwise.
 * Unlike listRows() this has a hard timeout: the overlay sits on every
 * page's read path, so a hung RDS must degrade to Supabase's rows in
 * bounded time. Callers chunk to <= 200 ids.
 *
 * @throws {PhiClientError} non-2xx; {PhiClientUnavailable} no session,
 *   unreachable, or timed out.
 */
export async function listRowsByIds(
  schema,
  table,
  ids,
  { timeoutMs = 5000 } = {},
) {
  if (!schema || !table) throw new Error("schema and table are required");
  const cleanIds = (ids || [])
    .filter((v) => v !== undefined && v !== null && v !== "")
    .map(String);
  if (!cleanIds.length) return [];
  const payload = await adminPost(
    `/phi/${encodeURIComponent(schema)}/${encodeURIComponent(table)}/list`,
    { ids: cleanIds, limit: 1000 },
    { timeoutMs },
  );
  return Array.isArray(payload?.rows) ? payload.rows : [];
}

/**
 * RDS's own live-activity aggregate (GET /admin/activity): what RDS actually
 * served over the last `window` seconds, from rds_access.audit_log -- counts
 * per family (read / write / delete / miss / admin), distinct viewers, a
 * timeline of slots and the newest events. Operator-gated by RDS (every
 * verified session is an operator). Table and bucket names, counts and
 * timestamps only; never a row.
 *
 * @param {{window?: number, tail?: number, timeoutMs?: number}} [opts]
 *   `window` must be one RDS accepts (60, 300, 900, 3600, 86400 seconds).
 * @throws {PhiClientError} non-2xx; {PhiClientUnavailable} no session,
 *   unreachable, or timed out.
 */
export async function getRdsActivity({ window = 300, tail = 20, timeoutMs = 8000 } = {}) {
  const qs = new URLSearchParams({ window: String(window), tail: String(tail) });
  return adminRequest("GET", `/admin/activity?${qs.toString()}`, undefined, { timeoutMs });
}
